import React, { useState, useEffect, useCallback } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Loader2, Save, Percent, Check, ToggleLeft, ToggleRight, Plus, X } from 'lucide-react';
import { configApi, FeeConfig } from '../../api/config.api';
import { useToast } from '../../hooks/use-toast';

// Transaction types for creating new fees
const TRANSACTION_TYPES = [
    { value: 'depo_sol', label: 'Depot Sol' },
    { value: 'depo_bs', label: 'Depot Ti Kanè' },
    { value: 'depo_mutuelle', label: 'Depot Mutuelle' },
    { value: 'depo_solide', label: 'Depot Solidè' },
    { value: 'retrait_sol', label: 'Retrait Sol' },
    { value: 'retrait_bs', label: 'Retrait Ti Kanè' },
    { value: 'retrait_mutuelle', label: 'Retrait Mutuelle' },
    { value: 'retrait_placement', label: 'Retrait Placement' },
    { value: 'placement', label: 'Placement' },
    { value: 'mutuelle', label: 'Mutuelle' },
    { value: 'pret', label: 'Prêt' },
    { value: 'transfer', label: 'Transfert' },
    { value: 'rembousement', label: 'Rembousement' },
    { value: 'paiement', label: 'Paiement' },
    { value: 'paiement_service', label: 'Paiement Service' },
    { value: 'commission_solide', label: 'Commission Solidè Agent' },
];

export function FeesConfig() {
    const { toast } = useToast();
    const [fees, setFees] = useState<FeeConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState<string | null>(null);
    const [editedValues, setEditedValues] = useState<Record<string, number>>({});
    const [showAddForm, setShowAddForm] = useState(false);
    const [newFee, setNewFee] = useState({ transaction_type: '', percentage: 0, description: '' });
    const [creating, setCreating] = useState(false);

    const loadFees = useCallback(async () => {
        try {
            setLoading(true);
            const response = await configApi.getFees();
            const data = response as any;
            const feesArray = Array.isArray(data) ? data : (data?.results || []);
            setFees(feesArray);
        } catch (err) {
            console.error('Error loading fees:', err);
            toast({ title: 'Erè', description: 'Pa kapab chaje konfigirasyon frais', type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        loadFees();
    }, [loadFees]);

    const handlePercentageChange = (type: string, value: string) => {
        const numValue = parseFloat(value) || 0;
        setEditedValues(prev => ({ ...prev, [type]: numValue }));
    };

    const handleSave = async (type: string) => {
        const newPercentage = editedValues[type];
        if (newPercentage === undefined) return;

        try {
            setSaving(type);
            await configApi.updateFee(type, { percentage: newPercentage });

            setFees(prev => prev.map(f =>
                f.transaction_type === type ? { ...f, percentage: newPercentage } : f
            ));
            setEditedValues(prev => {
                const newValues = { ...prev };
                delete newValues[type];
                return newValues;
            });
            toast({ title: 'Siksè', description: 'Frais aktyalize avèk siksè', type: 'success' });
        } catch (err) {
            console.error('Error saving fee:', err);
            toast({ title: 'Erè', description: 'Pa kapab sovgade frais', type: 'error' });
        } finally {
            setSaving(null);
        }
    };

    const handleToggleActive = async (type: string, isActive: boolean) => {
        try {
            await configApi.updateFee(type, { is_active: isActive });
            setFees(prev => prev.map(f =>
                f.transaction_type === type ? { ...f, is_active: isActive } : f
            ));
            toast({ title: 'Siksè', description: `Frais ${isActive ? 'aktive' : 'dezaktive'}`, type: 'success' });
        } catch (err) {
            console.error('Error toggling fee:', err);
            toast({ title: 'Erè', description: 'Pa kapab chanje estati frais', type: 'error' });
        }
    };

    const handleCreateFee = async () => {
        if (!newFee.transaction_type) {
            toast({ title: 'Erè', description: 'Chwazi yon tip tranzaksyon', type: 'error' });
            return;
        }

        try {
            setCreating(true);
            const created = await configApi.createFee({
                transaction_type: newFee.transaction_type,
                percentage: newFee.percentage,
                is_active: true,
                description: newFee.description || undefined,
            });
            setFees(prev => [...prev, created]);
            setNewFee({ transaction_type: '', percentage: 0, description: '' });
            setShowAddForm(false);
            toast({ title: 'Siksè', description: 'Nouvo frais kreye avèk siksè', type: 'success' });
        } catch (err: any) {
            console.error('Error creating fee:', err);
            const errorMsg = err.response?.data?.transaction_type?.[0] || 'Pa kapab kreye frais';
            toast({ title: 'Erè', description: errorMsg, type: 'error' });
        } finally {
            setCreating(false);
        }
    };

    const availableTypes = TRANSACTION_TYPES.filter(
        t => !fees.some(f => f.transaction_type === t.value)
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
                <span className="ml-3 text-slate-600">Chajman konfigirasyon frais...</span>
            </div>
        );
    }

    // Mobile Card Component
    const FeeCard = ({ fee }: { fee: FeeConfig }) => {
        const hasChanges = editedValues[fee.transaction_type] !== undefined &&
            editedValues[fee.transaction_type] !== fee.percentage;

        return (
            <div className="bg-white rounded-lg border p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="font-medium text-slate-900">{fee.transaction_type_display}</p>
                        {fee.description && (
                            <p className="text-xs text-slate-500 mt-0.5">{fee.description}</p>
                        )}
                    </div>
                    <button
                        onClick={() => handleToggleActive(fee.transaction_type, !fee.is_active)}
                        className="text-slate-600 hover:text-primary-600 transition-colors"
                    >
                        {fee.is_active ? (
                            <ToggleRight className="h-7 w-7 text-green-600" />
                        ) : (
                            <ToggleLeft className="h-7 w-7 text-slate-400" />
                        )}
                    </button>
                </div>

                <div className="flex items-center gap-3">
                    <div className="flex-1">
                        <label className="text-xs text-slate-500 mb-1 block">Pousantaj</label>
                        <div className="flex items-center gap-2">
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                className="h-9 text-center font-mono"
                                value={editedValues[fee.transaction_type] ?? fee.percentage}
                                onChange={(e) => handlePercentageChange(fee.transaction_type, e.target.value)}
                            />
                            <span className="text-slate-500">%</span>
                        </div>
                    </div>
                    <div className="pt-5">
                        <Button
                            size="sm"
                            disabled={!hasChanges || saving === fee.transaction_type}
                            onClick={() => handleSave(fee.transaction_type)}
                            className="gap-1.5"
                        >
                            {saving === fee.transaction_type ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : hasChanges ? (
                                <Save className="h-3.5 w-3.5" />
                            ) : (
                                <Check className="h-3.5 w-3.5" />
                            )}
                            {hasChanges ? 'Sovgade' : 'OK'}
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="p-4 sm:p-6">
            {/* Header info */}
            <div className="mb-4 sm:mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-primary-100 rounded-lg shrink-0">
                            <Percent className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-900">Frais Tranzaksyon</h3>
                            <p className="text-sm text-slate-600 mt-1">
                                Defini pousantaj frais pou chak tip tranzaksyon.
                            </p>
                        </div>
                    </div>
                    {availableTypes.length > 0 && (
                        <Button
                            size="sm"
                            onClick={() => setShowAddForm(!showAddForm)}
                            className="gap-1.5 w-full sm:w-auto"
                        >
                            <Plus className="h-4 w-4" />
                            Ajoute Frais
                        </Button>
                    )}
                </div>
            </div>

            {/* Add new fee form */}
            {showAddForm && (
                <div className="mb-4 sm:mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-slate-900">Ajoute Nouvo Frais</h4>
                        <button onClick={() => setShowAddForm(false)} className="text-slate-500 hover:text-slate-700">
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div>
                            <label className="text-sm text-slate-600 mb-1 block">Tip Tranzaksyon</label>
                            <select
                                className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm"
                                value={newFee.transaction_type}
                                onChange={(e) => setNewFee(prev => ({ ...prev, transaction_type: e.target.value }))}
                            >
                                <option value="">Chwazi...</option>
                                {availableTypes.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-sm text-slate-600 mb-1 block">Pousantaj (%)</label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                className="h-9"
                                value={newFee.percentage}
                                onChange={(e) => setNewFee(prev => ({ ...prev, percentage: parseFloat(e.target.value) || 0 }))}
                            />
                        </div>
                        <div className="sm:col-span-2 lg:col-span-1">
                            <label className="text-sm text-slate-600 mb-1 block">Deskripsyon (opsyonèl)</label>
                            <Input
                                className="h-9"
                                placeholder="Deskripsyon..."
                                value={newFee.description}
                                onChange={(e) => setNewFee(prev => ({ ...prev, description: e.target.value }))}
                            />
                        </div>
                        <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-1">
                            <Button onClick={handleCreateFee} disabled={creating} className="gap-1.5 flex-1 sm:flex-none">
                                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                                Kreye
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile/Tablet View - Cards */}
            <div className="lg:hidden grid grid-cols-1 gap-4">
                {fees.length === 0 ? (
                    <div className="col-span-full text-center py-8 text-slate-500">
                        Pa gen frais konfigire. Klike sou "Ajoute Frais" pou kòmanse.
                    </div>
                ) : (
                    fees.map((fee) => <FeeCard key={fee.transaction_type} fee={fee} />)
                )}
            </div>

            {/* Desktop View - Table */}
            <div className="hidden lg:block rounded-lg border overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-slate-50">
                            <TableHead className="font-semibold">Tip Tranzaksyon</TableHead>
                            <TableHead className="font-semibold text-center">Pousantaj (%)</TableHead>
                            <TableHead className="font-semibold text-center">Estati</TableHead>
                            <TableHead className="font-semibold text-right">Aksyon</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {fees.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                                    Pa gen frais konfigire. Klike sou "Ajoute Frais" pou kòmanse.
                                </TableCell>
                            </TableRow>
                        ) : fees.map((fee) => {
                            const hasChanges = editedValues[fee.transaction_type] !== undefined &&
                                editedValues[fee.transaction_type] !== fee.percentage;

                            return (
                                <TableRow key={fee.transaction_type} className="hover:bg-slate-50/50">
                                    <TableCell>
                                        <div>
                                            <p className="font-medium text-slate-900">{fee.transaction_type_display}</p>
                                            {fee.description && (
                                                <p className="text-xs text-slate-500 mt-0.5">{fee.description}</p>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                className="w-20 h-8 text-center font-mono"
                                                value={editedValues[fee.transaction_type] ?? fee.percentage}
                                                onChange={(e) => handlePercentageChange(fee.transaction_type, e.target.value)}
                                            />
                                            <span className="text-slate-500">%</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            <button
                                                onClick={() => handleToggleActive(fee.transaction_type, !fee.is_active)}
                                                className="text-slate-600 hover:text-primary-600 transition-colors"
                                            >
                                                {fee.is_active ? (
                                                    <ToggleRight className="h-7 w-7 text-green-600" />
                                                ) : (
                                                    <ToggleLeft className="h-7 w-7 text-slate-400" />
                                                )}
                                            </button>
                                            <Badge variant={fee.is_active ? 'default' : 'secondary'} className="text-xs">
                                                {fee.is_active ? 'Aktif' : 'Dezaktive'}
                                            </Badge>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            size="sm"
                                            disabled={!hasChanges || saving === fee.transaction_type}
                                            onClick={() => handleSave(fee.transaction_type)}
                                            className="gap-1.5"
                                        >
                                            {saving === fee.transaction_type ? (
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            ) : hasChanges ? (
                                                <Save className="h-3.5 w-3.5" />
                                            ) : (
                                                <Check className="h-3.5 w-3.5" />
                                            )}
                                            {hasChanges ? 'Sovgade' : 'OK'}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
